/**
 * Configurable prospect search filter definitions.
 * To add a new filter type:
 * 1. Add a new FilterGroupDef to the appropriate prospect type config
 * 2. Ensure the API key matches what the backend expects
 * 3. For multiselect with dynamic options, pass options from API response (see ProspectDatabaseSearchForm)
 */
import type { ProspectSearchFilterConfig } from '../types/prospectSearchFilters';

/**
 * Account search filters. Fundraising section is fully optional.
 * Add new groups (e.g. geography, company_size) by extending the groups array.
 */
export const getAccountSearchFilterConfig = (apiParams?: {
  last_fundraising_series?: string[];
  industry?: string[];
  country?: string[];
  size_range?: string[];
}): ProspectSearchFilterConfig => ({
  groups: [
    {
      id: 'industry',
      label: 'Industry',
      description: 'Required. Select one or more industries.',
      fields: [
        {
          id: 'industries',
          type: 'multiselect',
          label: 'Industries',
          apiKey: 'industries',
          options: apiParams?.industry ?? [],
          required: true
        }
      ]
    },
    {
      id: 'location',
      label: 'Location',
      description: 'Optional. Filter by HQ country.',
      fields: [
        {
          id: 'countries',
          type: 'multiselect',
          label: 'Countries',
          apiKey: 'countries',
          options: apiParams?.country ?? [],
          required: false
        }
      ]
    },
    {
      id: 'company_size',
      label: 'Company Size',
      description: 'Optional. Filter by employee count range.',
      fields: [
        {
          id: 'size_ranges',
          type: 'multiselect',
          label: 'Employee Count',
          apiKey: 'size_ranges',
          options: apiParams?.size_range ?? [],
          required: false
        }
      ]
    },
    {
      id: 'fundraising',
      label: 'Fundraising',
      description: 'Optional. Filter by funding history.',
      fields: [
        {
          id: 'last_fundraising_date',
          type: 'date',
          label: 'Last Fundraising Date',
          apiKey: 'last_fundraising_date',
          required: false
        },
        {
          id: 'last_fundraising_series',
          type: 'multiselect',
          label: 'Series',
          apiKey: 'last_fundraising_series',
          options: apiParams?.last_fundraising_series ?? [],
          required: false
        }
      ]
    }
  ],
  requireAtLeastOneFilter: false // industries is required per-field
});

/**
 * Lead search filters. Schema matches API-driven structure.
 * Options (labels, placeholders) can be extended; for now we use a simple config.
 */
export const getLeadSearchFilterConfig = (
  apiParams?: Record<
    string,
    { label: string; placeholder: string; description: string }
  >
): ProspectSearchFilterConfig => {
  const positionField = apiParams?.position ?? {
    label: 'Position / Title',
    placeholder: 'e.g. CEO; CTO; VP Engineering',
    description: 'Separate multiple positions with semicolons'
  };
  const locationField = apiParams?.location ?? {
    label: 'Location',
    placeholder: 'e.g. San Francisco, CA',
    description: 'City, region, or country'
  };

  return {
    groups: [
      {
        id: 'lead_criteria',
        label: 'Lead Criteria',
        description: 'Search by role and location',
        fields: [
          {
            id: 'positions',
            type: 'text',
            label: positionField.label,
            placeholder: positionField.placeholder,
            description: positionField.description,
            apiKey: 'positions',
            splitBy: ';',
            required: true
          },
          {
            id: 'location',
            type: 'text',
            label: locationField.label,
            placeholder: locationField.placeholder,
            description: locationField.description,
            apiKey: 'location',
            required: true
          }
        ]
      }
    ],
    requireAtLeastOneFilter: false // Lead has required fields per-field
  };
};
